import React, { useCallback } from 'react';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';
import { hasUserReacted } from '../../../utils/messageUtils';
import type { ReactionType, MessageReactions as MessageReactionsMap, Activity } from '../../../types/activities';

interface MessageReactionsProps {
  reactions?: MessageReactionsMap;
  currentUserId: string | undefined;
  onReaction: (reaction: ReactionType) => void;
  message?: Activity;
}

const REACTION_LABELS: Record<ReactionType, string> = {
  thumbs_up: '👍',
  thumbs_down: '👎',
  question: '❓',
  laugh: '😂',
};

export function MessageReactions({ reactions, currentUserId, onReaction, message }: MessageReactionsProps) {
  const handleReactionClick = useCallback(
    (reaction: ReactionType) => {
      onReaction(reaction);
    },
    [onReaction]
  );

  if (!reactions || Object.keys(reactions).length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 mt-2 flex-wrap">
      {(Object.keys(reactions) as ReactionType[]).map((reaction) => {
        const userIds = reactions[reaction] || [];
        const hasReacted = message 
          ? hasUserReacted(message, reaction, currentUserId)
          : (currentUserId && userIds.includes(currentUserId));
        const count = userIds.length;

        if (count === 0) return null;

        return (
          <Button
            key={reaction}
            variant={hasReacted ? 'default' : 'outline'}
            size="sm"
            className={cn(
              'h-6 px-2 text-xs gap-1',
              hasReacted && 'bg-primary text-primary-foreground'
            )}
            onClick={() => handleReactionClick(reaction)}
            aria-label={`${reaction} (${count})`}
          >
            <span>{REACTION_LABELS[reaction]}</span>
            <span>{count}</span>
          </Button>
        );
      })}
    </div>
  );
}
